import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
  OrgInvitationRole,
  OrgInvitationSummary,
  OrgMemberSummary,
} from '@nadoutong/contracts';
import { changePassword as apiChangePassword } from '../api/auth.ts';
import { PlatformApiError } from '../api/client.ts';
import {
  useCreateOrgInvitation,
  useOrgInvitations,
  useOrgMembers,
  useRevokeOrgInvitation,
} from '../api/useOrgs.ts';
import { Modal } from '../components/Modal.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { TechnicalDetails } from '../components/TechnicalDetails.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { formatTimestamp } from '../components/formatters.ts';
import { useClipboard } from '../hooks/useClipboard.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS } from '../styles/forms.ts';

/**
 * Settings page — read-only account info + in-session password change.
 *
 * Password change (R5 / PR-E.2) calls `POST /v1/auth/password/change` with
 * the caller's current + new passwords. The endpoint re-verifies the
 * current password server-side; a bad match renders the 401 envelope as
 * an inline "current password incorrect" error, keeping UI messaging
 * consistent with /login. Length is validated locally and on the server
 * (10–128 chars).
 */

const MIN_PASSWORD_LENGTH = 10;
const MAX_PASSWORD_LENGTH = 128;

function useChangePasswordMutation(): UseMutationResult<
  ChangePasswordResponse,
  Error,
  ChangePasswordRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (body) => apiChangePassword(body, fetchImpl),
  });
}

interface FieldErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

type Translate = ReturnType<typeof useT>;

function memberRoleLabel(t: Translate, role: OrgMemberSummary['role']): string {
  switch (role) {
    case 'owner':
      return t('settings.members.role.owner');
    case 'admin':
      return t('settings.members.role.admin');
    case 'member':
      return t('settings.members.role.member');
  }
}

function invitedRoleLabel(t: Translate, role: OrgInvitationRole): string {
  switch (role) {
    case 'admin':
      return t('settings.invite_modal.role.admin');
    case 'member':
      return t('settings.invite_modal.role.member');
  }
}

function isOrgInvitationRole(value: string): value is OrgInvitationRole {
  return value === 'admin' || value === 'member';
}

export function Settings(): JSX.Element {
  const { user } = useSession();
  const t = useT();

  return (
    <section data-testid="settings-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('settings.title')}
        </h2>
        <p className="text-sm text-outline">{t('settings.subtitle')}</p>
      </header>

      <article
        data-testid="settings-account-card"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      >
        <header>
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('settings.account.title')}
          </h3>
          <p className="mt-2 text-xs text-outline">{t('settings.account.description')}</p>
        </header>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <dt className="text-[10px] uppercase tracking-[0.1em] text-outline">
              {t('settings.account.email')}
            </dt>
            <dd
              className="font-mono text-sm text-on-surface"
              data-testid="settings-account-email"
            >
              {user?.email ?? '—'}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-[10px] uppercase tracking-[0.1em] text-outline">
              {t('settings.account.email_verified')}
            </dt>
            <dd data-testid="settings-email-verified">
              {user?.emailVerified === true ? (
                <StatusChip variant="ok" label={t('settings.account.status.verified')} />
              ) : (
                <StatusChip variant="warning" label={t('settings.account.status.unverified')} />
              )}
            </dd>
          </div>
        </dl>
      </article>

      <TechnicalDetails
        summary={t('settings.technical.summary')}
        testId="settings-technical-card"
      >
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <dt className="text-[10px] uppercase tracking-[0.1em] text-outline">
              {t('settings.account.account_id')}
            </dt>
            <dd
              className="font-mono text-sm text-on-surface-variant"
              data-testid="settings-account-id"
            >
              {user?.accountId ?? '—'}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-[10px] uppercase tracking-[0.1em] text-outline">
              {t('settings.account.org_id')}
            </dt>
            <dd
              className="font-mono text-sm text-on-surface-variant"
              data-testid="settings-org-id"
            >
              {user?.orgId ?? '—'}
            </dd>
          </div>
        </dl>
      </TechnicalDetails>

      <MembersCard />

      <ChangePasswordCard />
    </section>
  );
}

function MembersCard(): JSX.Element {
  const { user } = useSession();
  const t = useT();
  const orgId = user?.orgId ?? undefined;
  const membersQ = useOrgMembers(orgId);
  const invitationsQ = useOrgInvitations(orgId);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);

  const canManage = (() => {
    if (membersQ.data === undefined || user === null) {
      return false;
    }
    const self = membersQ.data.members.find((m) => m.accountId === user.accountId);
    if (self === undefined) {
      return false;
    }
    return self.role === 'owner' || self.role === 'admin';
  })();

  return (
    <article
      data-testid="settings-members-card"
      className="flex flex-col gap-5 rounded-xl bg-surface-container p-6"
    >
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('settings.members.title')}
          </h3>
          <p className="text-xs text-outline">{t('settings.members.subtitle')}</p>
        </div>
        {canManage ? (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            data-testid="members-invite-button"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-container px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
              person_add
            </span>
            <span>{t('settings.members.invite_button')}</span>
          </button>
        ) : null}
      </header>

      {membersQ.isLoading ? (
        <div className="flex flex-col gap-2" data-testid="members-loading">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10" />
          ))}
          <span className="sr-only">{t('settings.members.loading')}</span>
        </div>
      ) : membersQ.isError ? (
        <p className="text-sm text-error" role="alert" data-testid="members-error">
          {membersQ.error instanceof PlatformApiError
            ? `${membersQ.error.code}: ${membersQ.error.message}`
            : t('settings.members.error')}
        </p>
      ) : membersQ.data !== undefined ? (
        <MembersTable members={membersQ.data.members} />
      ) : null}

      {canManage ? (
        <InvitationsSection
          orgId={orgId}
          query={invitationsQ}
        />
      ) : null}

      {inviteOpen && orgId !== undefined ? (
        <InviteMemberModal
          orgId={orgId}
          onClose={() => setInviteOpen(false)}
          onCreated={(url) => {
            setInviteOpen(false);
            setLastInviteUrl(url);
          }}
        />
      ) : null}

      {lastInviteUrl !== null ? (
        <InviteLinkModal
          url={lastInviteUrl}
          onClose={() => setLastInviteUrl(null)}
        />
      ) : null}
    </article>
  );
}

interface MembersTableProps {
  readonly members: readonly OrgMemberSummary[];
}

function MembersTable({ members }: MembersTableProps): JSX.Element {
  const t = useT();
  if (members.length === 0) {
    return (
      <p className="text-sm text-outline" data-testid="members-empty">
        {t('settings.members.empty')}
      </p>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="members-table">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-outline">
            <th className="px-3 py-2 font-semibold">{t('settings.members.column.email')}</th>
            <th className="px-3 py-2 font-semibold">{t('settings.members.column.role')}</th>
            <th className="px-3 py-2 font-semibold">{t('settings.members.column.joined')}</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr
              key={m.accountId}
              data-testid={`members-row-${m.accountId}`}
              className="odd:bg-surface-container-low"
            >
              <td className="px-3 py-2 font-mono text-on-surface">{m.email}</td>
              <td className="px-3 py-2">
                <StatusChip
                  variant={m.role === 'owner' ? 'ok' : m.role === 'admin' ? 'neutral' : 'neutral'}
                  label={memberRoleLabel(t, m.role)}
                />
              </td>
              <td className="px-3 py-2 font-mono text-on-surface-variant">
                {formatTimestamp(m.joinedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface InvitationsSectionProps {
  readonly orgId: string | undefined;
  readonly query: ReturnType<typeof useOrgInvitations>;
}

function InvitationsSection({ orgId, query }: InvitationsSectionProps): JSX.Element {
  const t = useT();
  return (
    <section
      className="flex flex-col gap-3"
      data-testid="invitations-section"
    >
      <header>
        <h4 className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
          {t('settings.invitations.title')}
        </h4>
      </header>
      {query.isLoading ? (
        <Skeleton className="h-10" />
      ) : query.isError ? (
        <p className="text-sm text-error" role="alert" data-testid="invitations-error">
          {query.error instanceof PlatformApiError
            ? `${query.error.code}: ${query.error.message}`
            : t('settings.invitations.error')}
        </p>
      ) : query.data !== undefined ? (
        query.data.invitations.length === 0 ? (
          <p className="text-sm text-outline" data-testid="invitations-empty">
            {t('settings.invitations.empty')}
          </p>
        ) : (
          <InvitationsTable
            orgId={orgId ?? ''}
            invitations={query.data.invitations}
          />
        )
      ) : null}
    </section>
  );
}

interface InvitationsTableProps {
  readonly orgId: string;
  readonly invitations: readonly OrgInvitationSummary[];
}

function InvitationsTable({ orgId, invitations }: InvitationsTableProps): JSX.Element {
  const t = useT();
  const revokeM = useRevokeOrgInvitation();
  const toast = useToast();
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  async function handleRevoke(invitationId: string): Promise<void> {
    setRowError(null);
    try {
      await revokeM.mutateAsync({ orgId, invitationId });
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setRowError({ id: invitationId, message });
      toast.error(message);
    }
  }

  return (
    <div className="overflow-x-auto" data-testid="invitations-table">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-outline">
            <th className="px-3 py-2 font-semibold">{t('settings.invitations.column.email')}</th>
            <th className="px-3 py-2 font-semibold">{t('settings.invitations.column.role')}</th>
            <th className="px-3 py-2 font-semibold">
              {t('settings.invitations.column.invited_by')}
            </th>
            <th className="px-3 py-2 font-semibold">{t('settings.invitations.column.expires')}</th>
            <th className="px-3 py-2 font-semibold text-right">
              {t('settings.invitations.column.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((inv) => (
            <tr
              key={inv.id}
              data-testid={`invitations-row-${inv.id}`}
              className="odd:bg-surface-container-low"
            >
              <td className="px-3 py-2 font-mono text-on-surface">{inv.email}</td>
              <td className="px-3 py-2 text-on-surface-variant">{invitedRoleLabel(t, inv.role)}</td>
              <td className="px-3 py-2 font-mono text-on-surface-variant">
                {inv.invitedByEmail ?? inv.invitedByAccountId}
              </td>
              <td className="px-3 py-2 font-mono text-on-surface-variant">
                {formatTimestamp(inv.expiresAt)}
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => void handleRevoke(inv.id)}
                    disabled={revokeM.isPending}
                    data-testid={`invitations-revoke-${inv.id}`}
                    className="inline-flex items-center justify-center rounded-lg bg-error-container px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    {t('settings.invitations.revoke')}
                  </button>
                </div>
                {rowError !== null && rowError.id === inv.id ? (
                  <p
                    className="mt-1 text-[11px] font-medium text-error"
                    role="alert"
                    data-testid={`invitations-revoke-error-${inv.id}`}
                  >
                    {rowError.message}
                  </p>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface InviteMemberModalProps {
  readonly orgId: string;
  readonly onClose: () => void;
  readonly onCreated: (inviteUrl: string) => void;
}

function InviteMemberModal({
  orgId,
  onClose,
  onCreated,
}: InviteMemberModalProps): JSX.Element {
  const t = useT();
  const createM = useCreateOrgInvitation();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgInvitationRole>('member');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      setError(t('settings.invite_modal.email_required'));
      return;
    }
    try {
      const res = await createM.mutateAsync({ orgId, body: { email: trimmed, role } });
      toast.success(t('settings.invite_modal.toast.sent'));
      onCreated(res.inviteUrl);
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!createM.isPending) onClose();
      }}
      titleId="invite-member-modal-title"
      testId="members-invite-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2
          id="invite-member-modal-title"
          className="text-base font-semibold text-on-surface"
        >
          {t('settings.invite_modal.title')}
        </h2>
        <p className="text-sm text-outline">{t('settings.invite_modal.description')}</p>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('settings.invite_modal.email')}
          </span>
          <input
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={createM.isPending}
            required
            maxLength={254}
            data-testid="members-invite-email"
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('settings.invite_modal.role')}
          </span>
          <select
            value={role}
            onChange={(e) => {
              const nextRole = e.currentTarget.value;
              if (isOrgInvitationRole(nextRole)) {
                setRole(nextRole);
              }
            }}
            disabled={createM.isPending}
            data-testid="members-invite-role"
            className={INPUT_CLASS}
          >
            <option value="member">{t('settings.invite_modal.role.member')}</option>
            <option value="admin">{t('settings.invite_modal.role.admin')}</option>
          </select>
        </label>
        {error !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="members-invite-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={createM.isPending}
            data-testid="members-invite-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('settings.invite_modal.cancel')}
          </button>
          <button
            type="submit"
            disabled={createM.isPending}
            data-testid="members-invite-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
          >
            {createM.isPending
              ? t('settings.invite_modal.submitting')
              : t('settings.invite_modal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

interface InviteLinkModalProps {
  readonly url: string;
  readonly onClose: () => void;
}

function InviteLinkModal({ url, onClose }: InviteLinkModalProps): JSX.Element {
  const t = useT();
  const { copy, copied, error } = useClipboard();
  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="invite-link-modal-title"
      testId="members-invite-link-modal"
    >
      <div className="flex flex-col gap-6 p-8">
        <h2 id="invite-link-modal-title" className="text-base font-semibold text-on-surface">
          {t('settings.invite_link.title')}
        </h2>
        <p className="text-sm text-outline">{t('settings.invite_link.description')}</p>
        <button
          type="button"
          onClick={() => void copy(url)}
          data-testid="members-invite-link-copy"
          aria-label={t('settings.invite_link.aria_label')}
          className="group flex flex-col items-start gap-2 rounded-lg bg-surface-container-lowest p-4 text-left transition-all hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
            {t('settings.invite_link.click_to_copy')}
          </span>
          <span
            data-testid="members-invite-link-value"
            className="w-full break-all font-mono text-sm text-primary select-all"
          >
            {url}
          </span>
          <span
            data-testid="members-invite-link-copy-status"
            className={`text-[10px] uppercase tracking-[0.1em] ${
              copied ? 'text-tertiary' : 'text-outline'
            }`}
          >
            {copied
              ? t('settings.invite_link.copied')
              : t('settings.invite_link.copy_anywhere')}
          </span>
        </button>
        {error !== null ? (
          <p
            className="text-[11px] font-medium text-error"
            role="alert"
            data-testid="members-invite-link-copy-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="members-invite-link-done"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)]"
          >
            {t('settings.invite_link.done')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ChangePasswordCard(): JSX.Element {
  const t = useT();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const mutation = useChangePasswordMutation();

  function resetForm(): void {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFieldErrors({});
    setFormError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);
    setToast(null);

    const nextErrors: FieldErrors = {};
    if (currentPassword.length === 0) {
      nextErrors.currentPassword = t('settings.security.error.current_required');
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('settings.security.error.password_too_short', {
        min: String(MIN_PASSWORD_LENGTH),
      });
    } else if (newPassword.length > MAX_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('settings.security.error.password_too_long', {
        max: String(MAX_PASSWORD_LENGTH),
      });
    }
    if (newPassword !== confirmPassword) {
      nextErrors.confirmPassword = t('settings.security.error.mismatch');
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    try {
      await mutation.mutateAsync({ currentPassword, newPassword });
      setToast(t('settings.security.toast.success'));
      resetForm();
    } catch (err) {
      if (err instanceof PlatformApiError) {
        if (err.code === 'INVALID_CREDENTIALS') {
          setFieldErrors({
            currentPassword: t('settings.security.error.current_incorrect'),
          });
          return;
        }
        if (err.code === 'INVALID_PAYLOAD') {
          setFieldErrors({ newPassword: err.message });
          return;
        }
        setFormError(`${err.code}: ${err.message}`);
        return;
      }
      setFormError(t('settings.security.error.network'));
    }
  }

  const submitting = mutation.isPending;

  return (
    <article
      data-testid="settings-security-card"
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
    >
      <header>
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('settings.security.title')}
        </h3>
      </header>
      <form
        className="flex flex-col gap-6 rounded-lg bg-surface-container-low p-4"
        onSubmit={handleSubmit}
        data-testid="settings-change-password-form"
        noValidate
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-primary">
          {t('settings.security.change_password')}
        </span>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('settings.security.current_password')}
          </span>
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
            disabled={submitting}
            required
            data-testid="settings-current-password"
            className={INPUT_CLASS}
          />
          {fieldErrors.currentPassword !== undefined ? (
            <span
              role="alert"
              className="text-xs font-medium text-error"
              data-testid="settings-current-password-error"
            >
              {fieldErrors.currentPassword}
            </span>
          ) : null}
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('settings.security.new_password')}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            disabled={submitting}
            required
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            data-testid="settings-new-password"
            className={INPUT_CLASS}
          />
          {fieldErrors.newPassword !== undefined ? (
            <span
              role="alert"
              className="text-xs font-medium text-error"
              data-testid="settings-new-password-error"
            >
              {fieldErrors.newPassword}
            </span>
          ) : null}
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('settings.security.confirm_password')}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            disabled={submitting}
            required
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            data-testid="settings-confirm-password"
            className={INPUT_CLASS}
          />
          {fieldErrors.confirmPassword !== undefined ? (
            <span
              role="alert"
              className="text-xs font-medium text-error"
              data-testid="settings-confirm-password-error"
            >
              {fieldErrors.confirmPassword}
            </span>
          ) : null}
        </label>
        {formError !== null ? (
          <p
            role="alert"
            className="m-0 text-xs font-medium text-error"
            data-testid="settings-change-password-error"
          >
            {formError}
          </p>
        ) : null}
        {toast !== null ? (
          <p
            role="status"
            className="rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-medium text-primary"
            data-testid="settings-change-password-toast"
          >
            {toast}
          </p>
        ) : null}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={submitting}
            data-testid="settings-change-password-submit"
            className="inline-flex items-center justify-center rounded-xl bg-primary-container px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
          >
            {submitting ? t('settings.security.submitting') : t('settings.security.submit')}
          </button>
        </div>
      </form>
    </article>
  );
}
